import {
  ADMIN_ACCESS_PERMISSIONS,
  FULL_ACCESS_PERMISSIONS,
  getDefaultPermissionsForUserType,
} from "./registrationPermissions";

/**
 * Permission modules for team role create/edit.
 * Aligned with backend registration.js FULL_ACCESS_PERMISSIONS.
 */
export const PERMISSION_MODULES = [
  { label: "Dashboard", actions: ["Read"] },
  {
    label: "LiveChat",
    actions: ["Read", "Show_All_Intervened", "Transfer_Agent", "TakeOver"],
  },
  {
    label: "Campaign",
    actions: [
      "Read",
      "Create_CSV_Broadcast_Campaign",
      "Create_Api_Campaign",
      "Create_Broadcast_Campaign",
      "Create_Whatspp+SMS_Campaign",
      "Schedule_Campaign",
      "SMS_Template",
    ],
  },
  { label: "History", actions: ["Read"] },
  { label: "Contacts", actions: ["Read", "Create", "Download", "Archive"] },
  { label: "ArchivedContacts", actions: ["Read", "UnArchive"] },
  {
    label: "CannedMessage",
    actions: ["Read", "Create", "Delete", "Edit", "Favourite"],
  },
  { label: "whatsAppTemplate", actions: ["Read", "Create"] },
  { label: "SmsTemplate", actions: ["Read", "Create"] },
  { label: "Template_Lib", actions: ["Read"] },
  { label: "Team", actions: ["Read", "Create", "Edit", "Delete"] },
  { label: "Teamrole", actions: ["Read", "Create", "Edit", "Delete"] },
  { label: "Attribute", actions: ["Read"] },
  { label: "Allprojects", actions: ["Read"] },
  { label: "Profile", actions: ["Read", "Change_Password"] },
  { label: "Tag", actions: ["Read", "Create", "Edit", "Delete"] },
  { label: "APIKey", actions: ["Read", "Regenerate"] },
  { label: "WabaAnalytics", actions: ["Read"] },
  { label: "AgentActivity", actions: ["Read"] },
  { label: "Plans", actions: ["Read"] },
  { label: "Webhook", actions: ["Read", "Regenerate", "Subscribe"] },
  { label: "GreetingMessage", actions: ["Read"] },
  { label: "Opt-inManagement", actions: ["Read"] },
  { label: "Sendtestmessage", actions: ["Read"] },
  { label: "SMSConfig", actions: ["Read"] },
  { label: "GoogleSheetIntegration", actions: ["Read"] },
  { label: "GoogleFormConfig", actions: ["Read"] },
  { label: "Dialogflow", actions: ["Read"] },
  { label: "PaymentsSetup", actions: ["Read"] },
  { label: "Payments", actions: ["Read"] },
];

export const permissionKey = (label, action) => `${label}_${action}`;

/**
 * Modules an actor may assign when creating/editing a team role.
 * Superadmin → Full Access set; admin → Admin Access set only.
 */
export function getAssignablePermissionModules(userType) {
  const defaults =
    getDefaultPermissionsForUserType(userType) || ADMIN_ACCESS_PERMISSIONS;
  const allowed = new Set(
    Object.keys(defaults).filter((key) => defaults[key]),
  );

  return PERMISSION_MODULES.map((mod) => {
    const actions = mod.actions.filter((action) =>
      allowed.has(permissionKey(mod.label, action)),
    );
    return actions.length ? { ...mod, actions } : null;
  }).filter(Boolean);
}

/** Drop permission flags the current user is not allowed to grant. */
export function sanitizeAssignablePermissions(permissions, userType) {
  const defaults =
    getDefaultPermissionsForUserType(userType) || ADMIN_ACCESS_PERMISSIONS;
  const next = {};
  Object.entries(permissions || {}).forEach(([key, value]) => {
    if (value && defaults[key]) next[key] = true;
  });
  return next;
}

export { ADMIN_ACCESS_PERMISSIONS, FULL_ACCESS_PERMISSIONS };

const isPermissionKey = (key) =>
  typeof key === "string" && /^[A-Za-z][A-Za-z0-9+-]*_/.test(key);

const parseJsonLoose = (value) => {
  let current = value;
  for (let i = 0; i < 5; i += 1) {
    if (typeof current !== "string") return current;
    const trimmed = current.trim();
    if (!trimmed) return {};
    try {
      current = JSON.parse(trimmed);
    } catch {
      return trimmed;
    }
  }
  return current;
};

/** Flatten login `teamroles.permissions` (string, array of JSON, or nested object). */
export const flattenPermissions = (value, acc = {}) => {
  if (value == null) return acc;

  const parsed = typeof value === "string" ? parseJsonLoose(value) : value;

  if (typeof parsed === "string") {
    if (isPermissionKey(parsed)) acc[parsed] = true;
    return acc;
  }

  if (Array.isArray(parsed)) {
    parsed.forEach((entry) => flattenPermissions(entry, acc));
    return acc;
  }

  if (!parsed || typeof parsed !== "object") return acc;

  if (Object.prototype.hasOwnProperty.call(parsed, "permissions")) {
    flattenPermissions(parsed.permissions, acc);
  }

  Object.entries(parsed).forEach(([key, val]) => {
    if (key === "permissions") return;
    if (isPermissionKey(key)) {
      acc[key] = val;
      return;
    }
    if (val && typeof val === "object" && !Array.isArray(val)) {
      Object.entries(val).forEach(([action, flag]) => {
        if (action) acc[`${key}_${action}`] = flag;
      });
    }
  });

  return acc;
};

export const parseRolePermissions = (role) => {
  if (role == null) return {};
  if (typeof role === "string" || Array.isArray(role)) {
    return flattenPermissions(role);
  }
  if (typeof role !== "object") return {};
  if (role.permissions != null) return flattenPermissions(role.permissions);
  return flattenPermissions(role);
};
